/**
 * A timeout that keeps its schedule in a background tab (TRUS-668).
 *
 * Browsers throttle page timers in hidden tabs: Chrome's "intensive throttling"
 * runs a chained setTimeout at most once a MINUTE after a tab has been hidden
 * for five minutes, and some engines pause them longer. The notification poll
 * is a chained timeout, so a message in a background tab could take a minute or
 * more to show. Timers inside a dedicated Web Worker are not subject to that
 * throttling, so while the page is hidden the delay is kept by a tiny worker
 * (an inline blob script; CSP allows `worker-src 'self' blob:`) that only posts
 * back "fire". The callback still runs on the page.
 *
 * Where a worker cannot be created (tests, a locked-down browser) this is a
 * plain setTimeout. While the page is visible it is always a plain setTimeout.
 */

const WORKER_SOURCE = `
var timers = {};
onmessage = function (e) {
  var d = e.data || {};
  if (d.cancel) { clearTimeout(timers[d.id]); delete timers[d.id]; return; }
  timers[d.id] = setTimeout(function () { delete timers[d.id]; postMessage(d.id); }, d.ms);
};
`;

let worker: Worker | null | undefined;
let nextId = 1;
const callbacks = new Map<number, () => void>();

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  worker = null;
  try {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL?.createObjectURL !== 'function') {
      return worker;
    }
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    const w = new Worker(url);
    w.onmessage = (e: MessageEvent) => {
      const id = Number(e.data);
      const cb = callbacks.get(id);
      callbacks.delete(id);
      cb?.();
    };
    w.onerror = () => {
      // The worker is unusable: fire what it held now (the poll re-arms itself)
      // and use plain timers from here on.
      worker = null;
      const pending = Array.from(callbacks.values());
      callbacks.clear();
      pending.forEach((cb) => cb());
    };
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
}

function pageHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

/** Run `fn` after `ms`, unthrottled while the page is hidden. Returns a cancel function. */
export function backgroundTimeout(fn: () => void, ms: number): () => void {
  const w = pageHidden() ? getWorker() : null;
  if (!w) {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
  }
  const id = nextId++;
  callbacks.set(id, fn);
  try {
    w.postMessage({ id, ms });
  } catch {
    callbacks.delete(id);
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
  }
  return () => {
    if (!callbacks.delete(id)) return;
    try {
      w.postMessage({ id, cancel: true });
    } catch {
      /* ignore */
    }
  };
}

/** Tests only. */
export function __resetBackgroundTimer(): void {
  try {
    worker?.terminate();
  } catch {
    /* ignore */
  }
  worker = undefined;
  callbacks.clear();
}
